// Sunworker Site Guard: Überwachung · Verlauf/Diff · Backups · Schadcode · Reparatur-Set (externer Scan + KI-Diagnose)
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  ShieldCheck, ShieldAlert, Search, Loader2, Plus, Check, X, RotateCcw, Archive, ThumbsUp, Bot,
  Globe, Lock, FileCode2, Download, Copy, Stethoscope, Trash2, Activity
} from "lucide-react";

const BASE = process.env.REACT_APP_BACKEND_URL;
const API = `${BASE}/api/siteguard`;
const SEV = {
  critical: { label: "KRITISCH", color: "#F43F5E" }, high: { label: "HOCH", color: "#F97316" },
  medium: { label: "MITTEL", color: "#F59E0B" }, low: { label: "NIEDRIG", color: "#38BDF8" }, info: { label: "INFO", color: "#9CA3AF" },
};
const scoreColor = (s) => (s >= 85 ? "#10B981" : s >= 60 ? "#F59E0B" : "#F43F5E");

export function SiteGuard() {
  const [sites, setSites] = useState([]);
  const [sel, setSel] = useState(null);
  const [msg, setMsg] = useState("");
  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(""), 4000); };
  const load = useCallback(async () => {
    const r = await axios.get(`${API}/sites`);
    setSites(r.data);
    setSel((s) => s || r.data[0]?.id || null);
  }, []);
  useEffect(() => { load(); }, [load]);
  const site = sites.find((s) => s.id === sel);

  return (
    <div className="space-y-5" data-testid="siteguard-view">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-heading font-bold text-2xl flex items-center gap-2"><ShieldCheck size={22} className="text-eb-primary" /> Site Guard</h2>
          <p className="text-eb-muted text-sm">WordPress &amp; Divi überwachen · Änderungsverlauf · Backups (Cloud + lokal) · Schadcode erkennen · auf funktionierende Version zurücksetzen</p>
        </div>
        {msg && <span className="text-emerald-400 text-xs" data-testid="siteguard-msg">{msg}</span>}
      </div>
      <RepairKit sites={sites} flash={flash} onDone={load} />
      <div className="grid grid-cols-1 xl:grid-cols-[300px_1fr] gap-5">
        <SiteList sites={sites} sel={sel} setSel={setSel} reload={load} flash={flash} />
        {site && <SiteDetail key={site.id} site={site} reload={load} flash={flash} />}
      </div>
    </div>
  );
}

/* ---------------- Reparatur-Set ---------------- */
function RepairKit({ sites, flash, onDone }) {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState("");
  const [scan, setScan] = useState(null);
  const [plan, setPlan] = useState(null);
  const run = async () => {
    setBusy("scan"); setPlan(null);
    try { const r = await axios.post(`${API}/scan`, { url, max_pages: 15 }); setScan(r.data); onDone(); }
    catch (e) { flash(e.response?.data?.detail || "Scan fehlgeschlagen"); }
    finally { setBusy(""); }
  };
  const diagnose = async () => {
    setBusy("diag");
    try { setPlan((await axios.post(`${API}/diagnose/${scan.id}`)).data); }
    catch (e) { flash(e.response?.data?.detail || "Diagnose fehlgeschlagen"); }
    finally { setBusy(""); }
  };
  const queue = async () => {
    try { const r = await axios.post(`${API}/diagnose/${scan.id}/queue`); flash(`✓ ${r.data.queued} Reparaturschritte zur Freigabe angelegt`); onDone(); }
    catch (e) { flash(e.response?.data?.detail || "Fehler"); }
  };
  const addSite = async () => {
    try { await axios.post(`${API}/sites`, { url: scan.url }); flash("✓ Website in Site Guard aufgenommen"); onDone(); }
    catch (e) { flash(e.response?.data?.detail || "Fehler"); }
  };
  const known = scan && sites.some((s) => s.url.replace("www.", "") === scan.url.replace(/\/$/, "").replace("www.", ""));
  return (
    <div className="card space-y-4" data-testid="repair-kit">
      <div className="flex items-end gap-2 flex-wrap">
        <div className="flex-1 min-w-[260px]">
          <div className="overline mb-1 flex items-center gap-1"><Stethoscope size={12} /> Unabhängiges Reparatur-Set – beliebige Website prüfen</div>
          <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="z. B. strandhus-ruegen.de"
            onKeyDown={(e) => e.key === "Enter" && url && run()} data-testid="repair-url-input" />
        </div>
        <button className="btn btn-primary" onClick={run} disabled={!url || busy === "scan"} data-testid="repair-scan-btn">
          {busy === "scan" ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />} Website prüfen
        </button>
      </div>
      {scan && <ScanResult scan={scan} />}
      {scan && (
        <div className="flex gap-2 flex-wrap">
          <button className="btn" onClick={diagnose} disabled={busy === "diag"} data-testid="repair-diagnose-btn">
            {busy === "diag" ? <Loader2 size={14} className="animate-spin" /> : <Bot size={14} />} KI-Diagnose &amp; Reparaturplan
          </button>
          {!known && <button className="btn" onClick={addSite} data-testid="repair-add-site-btn"><Plus size={14} /> Dauerhaft überwachen</button>}
        </div>
      )}
      {plan && <RepairPlan plan={plan} canQueue={!!scan.site_id} onQueue={queue} />}
    </div>
  );
}

function ScanResult({ scan }) {
  const [open, setOpen] = useState(false);
  if (!scan.reachable) return <div className="text-rose-400 text-sm" data-testid="scan-unreachable">Nicht erreichbar: {scan.error}</div>;
  const counts = scan.findings.reduce((a, f) => ({ ...a, [f.severity]: (a[f.severity] || 0) + 1 }), {});
  return (
    <div className="space-y-3" data-testid="scan-result">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="eb-surface rounded-md p-3 text-center">
          <div className="overline">Gesundheit</div>
          <div className="font-heading font-bold text-3xl" style={{ color: scoreColor(scan.score) }} data-testid="scan-score">{scan.score}</div>
        </div>
        <Info icon={Globe} label="System" value={scan.cms.wordpress ? `WordPress${scan.cms.divi ? " + Divi" : ""}` : "kein WordPress erkannt"} sub={scan.cms.server || ""} />
        <Info icon={Lock} label="SSL" value={scan.ssl.valid ? `gültig · ${scan.ssl.days_left} Tage` : "ungültig"} sub={scan.ssl.issuer || scan.ssl.error || ""} />
        <Info icon={Activity} label="Seiten geprüft" value={scan.pages.length} sub={`${Math.round(scan.duration_ms / 100) / 10} s Scan`} />
        <Info icon={ShieldAlert} label="Befunde" value={scan.findings.length}
          sub={Object.entries(counts).map(([k, v]) => `${v} ${SEV[k].label.toLowerCase()}`).join(" · ") || "keine"} />
      </div>
      <div className="space-y-1">
        {scan.findings.length === 0 && <div className="text-sm text-emerald-400">Keine Probleme gefunden ✓</div>}
        {scan.findings.map((f) => (
          <div key={f.id} className="flex gap-3 items-start p-2 rounded eb-surface text-xs" data-testid={`finding-${f.id}`}>
            <span className="channel-pill shrink-0" style={{ background: SEV[f.severity].color + "22", color: SEV[f.severity].color }}>{SEV[f.severity].label}</span>
            <div className="min-w-0">
              <div className="font-semibold">{f.category} · {f.title}</div>
              <div className="text-eb-muted break-all">{f.detail}</div>
              <div className="text-[10px] font-mono text-eb-muted truncate">{f.page}</div>
            </div>
          </div>
        ))}
      </div>
      <button className="text-[11px] text-eb-primary" onClick={() => setOpen(!open)} data-testid="scan-pages-toggle">{open ? "Seiten ausblenden" : "Geprüfte Seiten anzeigen"}</button>
      {open && (
        <div className="font-mono text-[11px] space-y-0.5">
          {scan.pages.map((p) => (
            <div key={p.url} className="flex gap-3"><span className={p.status >= 400 || !p.status ? "text-rose-400" : "text-emerald-400"}>{p.status || "ERR"}</span>
              <span className="w-16 text-eb-muted">{p.ms} ms</span><span className="truncate">{p.url}</span></div>
          ))}
        </div>
      )}
    </div>
  );
}

function Info({ icon: Icon, label, value, sub }) {
  return (
    <div className="eb-surface rounded-md p-3">
      <div className="overline flex items-center gap-1"><Icon size={11} /> {label}</div>
      <div className="font-semibold text-sm mt-1">{value}</div>
      <div className="text-[10px] text-eb-muted truncate">{sub}</div>
    </div>
  );
}

function RepairPlan({ plan, canQueue, onQueue }) {
  return (
    <div className="eb-surface rounded-md p-4 space-y-3" data-testid="repair-plan">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="overline">KI-Diagnose · Risiko: <span className="text-eb-primary">{plan.risk}</span></div>
        {canQueue && plan.steps?.some((s) => s.auto) && (
          <button className="btn btn-primary !py-1 text-xs" onClick={onQueue} data-testid="repair-queue-btn"><Check size={12} /> Auto-Schritte zur Freigabe</button>
        )}
      </div>
      <div className="text-sm whitespace-pre-line">{plan.summary}</div>
      <ol className="space-y-2">
        {(plan.steps || []).map((s, i) => (
          <li key={i} className="text-xs border-l-2 border-eb-primary/50 pl-3" data-testid={`repair-step-${i}`}>
            <div className="font-semibold">{i + 1}. {s.title} {s.auto ? <span className="text-emerald-400">· automatisch reparierbar</span> : <span className="text-amber-400">· manuell</span>}</div>
            <div className="text-eb-muted">{s.why}</div>
            <div>{s.fix}</div>
            {s.code && <pre className="mt-1 p-2 rounded bg-black/40 font-mono text-[10px] overflow-x-auto">{s.code}</pre>}
          </li>
        ))}
      </ol>
    </div>
  );
}

/* ---------------- Site list ---------------- */
function SiteList({ sites, sel, setSel, reload, flash }) {
  const [url, setUrl] = useState("");
  const add = async () => {
    try { const r = await axios.post(`${API}/sites`, { url }); setUrl(""); await reload(); setSel(r.data.id); flash("✓ Website angelegt – jetzt Plugin verbinden"); }
    catch (e) { flash(e.response?.data?.detail || "Fehler"); }
  };
  return (
    <div className="space-y-2" data-testid="site-list">
      {sites.map((s) => (
        <button key={s.id} onClick={() => setSel(s.id)} data-testid={`site-${s.id}`}
          className={`card !p-3 w-full text-left transition-colors ${sel === s.id ? "!border-eb-primary" : ""}`}>
          <div className="flex justify-between items-start gap-2">
            <div className="min-w-0">
              <div className="font-semibold text-sm truncate">{s.name}</div>
              <div className="text-[10px] font-mono text-eb-muted truncate">{s.url}</div>
            </div>
            {s.health_score != null && <span className="font-heading font-bold" style={{ color: scoreColor(s.health_score) }}>{s.health_score}</span>}
          </div>
          <div className="flex gap-2 mt-2 text-[10px]">
            <span className={s.agent_connected ? "text-emerald-400" : "text-eb-muted"}>{s.agent_connected ? "● Plugin aktiv" : "○ nur extern"}</span>
            {s.open_issues > 0 && <span className="text-rose-400">{s.open_issues} Warnungen</span>}
            {s.pending_actions > 0 && <span className="text-amber-400">{s.pending_actions} Freigaben</span>}
            <span className="text-eb-muted">{s.mode === "auto" ? "Autopilot" : "Freigabe"}</span>
          </div>
        </button>
      ))}
      <div className="card !p-3 flex gap-1">
        <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="neue Website-URL" data-testid="site-add-input" />
        <button className="btn !px-2" onClick={add} disabled={!url} data-testid="site-add-btn"><Plus size={14} /></button>
      </div>
    </div>
  );
}

/* ---------------- Site detail ---------------- */
function SiteDetail({ site, reload, flash }) {
  const [tab, setTab] = useState("alerts");
  const [busy, setBusy] = useState(false);
  const setMode = async (mode) => {
    try { await axios.put(`${API}/sites/${site.id}`, { mode }); reload(); flash(mode === "auto" ? "Autopilot aktiv – Reparaturen laufen ohne Rückfrage" : "Warnung + Freigabe aktiv"); }
    catch (e) { flash(e.response?.data?.detail || "Fehler"); }
  };
  const scan = async () => {
    setBusy(true);
    try { const r = await axios.post(`${API}/sites/${site.id}/scan`); flash(`✓ Scan: Score ${r.data.score}, ${r.data.findings.length} Befunde`); reload(); }
    catch (e) { flash(e.response?.data?.detail || "Scan fehlgeschlagen"); }
    finally { setBusy(false); }
  };
  const del = async () => {
    if (!window.confirm(`${site.name} aus Site Guard entfernen?`)) return;
    try { await axios.delete(`${API}/sites/${site.id}`); reload(); }
    catch (e) { flash(e.response?.data?.detail || "Fehler"); }
  };
  const tabs = [["alerts", "Warnungen & Freigaben"], ["history", "Verlauf & Versionen"], ["connect", "Plugin verbinden"], ["log", "Protokoll"]];
  return (
    <div className="space-y-4" data-testid="site-detail">
      <div className="card flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="font-heading font-bold text-lg">{site.name}</div>
          <div className="text-xs text-eb-muted">{site.url} · {site.wp_version ? `WP ${site.wp_version} · PHP ${site.php_version} · ${site.theme}${site.divi ? " (Divi)" : ""}` : "noch kein Plugin-Bericht"}</div>
        </div>
        <div className="flex gap-2 items-center">
          <div className="flex gap-1 eb-surface rounded-full p-1">
            {[["approval", "Warnung + Freigabe"], ["auto", "Autopilot"]].map(([m, l]) => (
              <button key={m} onClick={() => setMode(m)} data-testid={`site-mode-${m}`}
                className={`px-3 py-0.5 rounded-full text-xs font-semibold ${site.mode === m ? "bg-eb-primary text-[#0B0F17]" : "text-eb-muted"}`}>{l}</button>
            ))}
          </div>
          <button className="btn" onClick={scan} disabled={busy} data-testid="site-scan-btn">{busy ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />} Scannen</button>
          <button className="text-eb-muted hover:text-rose-400" onClick={del} data-testid="site-delete-btn"><Trash2 size={14} /></button>
        </div>
      </div>
      <div className="flex gap-1 eb-surface rounded-lg p-1 w-fit">
        {tabs.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`site-tab-${k}`}
            className={`px-3 py-1.5 rounded-md text-xs font-semibold ${tab === k ? "bg-eb-primary text-[#0B0F17]" : "text-eb-muted"}`}>{l}</button>
        ))}
      </div>
      {tab === "alerts" && <AlertsTab site={site} reload={reload} flash={flash} />}
      {tab === "history" && <HistoryTab site={site} reload={reload} flash={flash} />}
      {tab === "connect" && <ConnectTab site={site} flash={flash} />}
      {tab === "log" && <LogTab site={site} />}
    </div>
  );
}

function AlertsTab({ site, reload, flash }) {
  const [actions, setActions] = useState([]);
  const load = useCallback(async () => setActions((await axios.get(`${API}/sites/${site.id}/actions`)).data), [site.id]);
  useEffect(() => { load(); }, [load]);
  const act = async (id, what) => {
    try { await axios.post(`${API}/actions/${id}/${what}`); flash(what === "approve" ? "✓ Freigegeben – Plugin führt aus" : "Abgelehnt"); load(); reload(); }
    catch (e) { flash(e.response?.data?.detail || "Fehler"); }
  };
  const pending = actions.filter((a) => a.status === "pending");
  const STATUS = { queued: "wartet auf Plugin", sent: "an Plugin gesendet", done: "erledigt ✓", failed: "fehlgeschlagen", rejected: "abgelehnt" };
  return (
    <div className="space-y-3" data-testid="siteguard-alerts">
      {pending.length === 0 && <div className="card text-sm text-emerald-400" data-testid="sg-no-pending">Keine offenen Freigaben.</div>}
      {pending.map((a) => (
        <div key={a.id} className="card !p-3 flex items-start justify-between gap-3" style={{ borderLeft: "3px solid #F43F5E" }} data-testid={`sg-action-${a.id}`}>
          <div className="flex gap-2"><ShieldAlert size={18} className="text-rose-400 shrink-0 mt-0.5" />
            <div><div className="text-[10px] font-mono text-eb-muted uppercase">{a.command} · {a.target}</div><div className="text-sm">{a.protocol}</div></div>
          </div>
          <div className="flex gap-1 shrink-0">
            <button className="btn !py-1 !px-2 text-xs !border-emerald-500 text-emerald-400" onClick={() => act(a.id, "approve")} data-testid={`sg-approve-${a.id}`}><Check size={12} /> OK</button>
            <button className="btn btn-danger !py-1 !px-2 text-xs" onClick={() => act(a.id, "reject")} data-testid={`sg-reject-${a.id}`}><X size={12} /></button>
          </div>
        </div>
      ))}
      {actions.filter((a) => a.status !== "pending").length > 0 && (
        <div className="card">
          <div className="overline mb-2">Ausgeführte Reparaturen</div>
          {actions.filter((a) => a.status !== "pending").slice(0, 20).map((a) => (
            <div key={a.id} className="flex justify-between text-xs py-1 border-b border-white/5">
              <span className="truncate">{a.command} · {a.target}</span>
              <span className={a.status === "done" ? "text-emerald-400" : a.status === "failed" || a.status === "rejected" ? "text-rose-400" : "text-amber-400"}>{STATUS[a.status]}{a.result ? ` · ${a.result}` : ""}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function HistoryTab({ site, reload, flash }) {
  const [events, setEvents] = useState([]);
  const [diff, setDiff] = useState(null);
  const load = useCallback(async () => setEvents((await axios.get(`${API}/sites/${site.id}/events`)).data), [site.id]);
  useEffect(() => { load(); }, [load]);
  const showDiff = async (id) => setDiff((await axios.get(`${API}/events/${id}/diff`)).data);
  const repair = async (id, target) => {
    try {
      const r = await axios.post(`${API}/events/${id}/repair`, { target });
      flash(target === "accept" ? "✓ Änderung als korrekt übernommen" : r.data.action?.status === "queued" ? "✓ Autopilot: Reparatur gestartet" : "✓ Reparatur zur Freigabe angelegt");
      load(); reload();
    } catch (e) { flash(e.response?.data?.detail || "Fehler"); }
  };
  const TYPE = { modified: "geändert", added: "neu", deleted: "gelöscht", core_mismatch: "Core verändert", fatal_error: "PHP-Fehler", new_admin: "neuer Admin" };
  return (
    <div className="space-y-2" data-testid="siteguard-history">
      {events.length === 0 && <div className="card text-sm text-eb-muted">Noch keine Änderungen aufgezeichnet.</div>}
      {events.map((e) => (
        <div key={e.id} className="card !p-3" data-testid={`sg-event-${e.id}`}>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-xs">
                <span className="channel-pill" style={{ background: SEV[e.severity].color + "22", color: SEV[e.severity].color }}>{SEV[e.severity].label}</span>
                <span className="font-mono text-eb-muted">{e.at.slice(0, 16).replace("T", " ")}</span>
                <span>{TYPE[e.type] || e.type}</span>
                <span className={e.status === "open" ? "text-rose-400" : "text-emerald-400"}>{e.status === "open" ? "offen" : e.status === "info" ? "unauffällig" : e.status === "accepted" ? "akzeptiert" : "repariert"}</span>
              </div>
              <div className="font-mono text-xs mt-1 flex items-center gap-1"><FileCode2 size={12} /> {e.path}</div>
              {e.issues.map((i, k) => <div key={k} className="text-[11px] text-rose-300 mt-0.5">⚠ {i.title}{i.snippet ? ` – ${i.snippet.slice(0, 120)}` : ""}</div>)}
            </div>
            <div className="flex gap-1 flex-wrap">
              {e.version_id && <button className="btn !py-1 !px-2 text-xs" onClick={() => showDiff(e.id)} data-testid={`sg-diff-${e.id}`}><FileCode2 size={12} /> Alt ↔ Neu</button>}
              {e.status === "open" && e.prev_version_id && (
                <button className="btn !py-1 !px-2 text-xs" onClick={() => repair(e.id, "last_good")} data-testid={`sg-restore-${e.id}`}><RotateCcw size={12} /> Funktionierende Version</button>
              )}
              {e.status === "open" && <button className="btn !py-1 !px-2 text-xs" onClick={() => repair(e.id, "quarantine")} data-testid={`sg-quarantine-${e.id}`}><Archive size={12} /> {e.type === "new_admin" ? "Konto entfernen" : "Quarantäne"}</button>}
              {(e.status === "open" || e.status === "info") && e.version_id && (
                <button className="btn !py-1 !px-2 text-xs" onClick={() => repair(e.id, "accept")} data-testid={`sg-accept-${e.id}`}><ThumbsUp size={12} /> Neu ist OK</button>
              )}
            </div>
          </div>
        </div>
      ))}
      {diff && <DiffModal diff={diff} onClose={() => setDiff(null)} />}
    </div>
  );
}

function DiffModal({ diff, onClose }) {
  const col = { add: "bg-emerald-500/15 text-emerald-200", del: "bg-rose-500/15 text-rose-200", eq: "text-eb-muted", skip: "text-eb-muted italic" };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6" onClick={onClose} data-testid="sg-diff-modal">
      <div className="card max-w-5xl w-full max-h-[85vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-3">
          <div>
            <div className="font-mono text-sm">{diff.event.path}</div>
            <div className="text-[11px] text-eb-muted">Alt: {diff.old_at?.slice(0, 16).replace("T", " ") || "–"} · Neu: {diff.new_at?.slice(0, 16).replace("T", " ") || "–"}</div>
          </div>
          <button onClick={onClose} data-testid="sg-diff-close"><X size={16} /></button>
        </div>
        <pre className="font-mono text-[11px] leading-relaxed">
          {diff.rows.map((r, i) => (
            <div key={i} className={`px-2 ${col[r.t]}`}>
              {r.t === "skip" ? `… ${r.n} unveränderte Zeilen …` : `${r.t === "add" ? "+" : r.t === "del" ? "−" : " "} ${String(r.a || r.b || "").padStart(4)}  ${r.line}`}
            </div>
          ))}
        </pre>
      </div>
    </div>
  );
}

function ConnectTab({ site, flash }) {
  const [key, setKey] = useState("");
  const [pluginKey, setPluginKey] = useState("");
  useEffect(() => {
    axios.get(`${API}/sites/${site.id}/agent-key`).then((r) => setKey(r.data.agent_key)).catch(() => setKey("nur für Administratoren"));
    axios.get(`${BASE}/api/auth/plugin-key`).then((r) => setPluginKey(r.data.key)).catch(() => setPluginKey(""));
  }, [site.id]);
  const copy = (t) => { navigator.clipboard.writeText(t); flash("✓ Kopiert"); };
  return (
    <div className="card space-y-3 text-sm" data-testid="siteguard-connect">
      <div className="overline">WordPress-Plugin verbinden</div>
      <ol className="list-decimal pl-5 space-y-1 text-xs">
        <li>Plugin herunterladen und in WordPress unter <em>Plugins → Installieren → Hochladen</em> installieren (eigenständig oder als Teil von easybooking-wp).</li>
        <li>In WordPress <em>Werkzeuge/easybooking → Site Guard</em> öffnen und Cloud-URL + Schlüssel eintragen.</li>
        <li>„Jetzt scannen“ klicken – der erste Scan wird als funktionierender Stand gespeichert, ab dann wird jede Änderung verfolgt.</li>
      </ol>
      <div className="flex gap-2 flex-wrap">
        <a className="btn btn-primary" href={`${API}/download/sunworker-site-guard.zip`} data-testid="download-siteguard-zip"><Download size={14} /> Site Guard (eigenständig)</a>
        <a className="btn" href={`${API}/download/easybooking-wp.zip`} data-testid="download-easybooking-zip"><Download size={14} /> easybooking-wp (inkl. Site Guard)</a>
      </div>
      {[["Cloud-URL", BASE], ["Site-Guard-Schlüssel", key], ["Werbe-Alarm-Schlüssel (easybooking → Einstellungen)", pluginKey]].map(([l, v]) => (
        <div key={l}>
          <div className="overline mb-1">{l}</div>
          <div className="flex gap-1"><input readOnly value={v || ""} className="font-mono !text-xs" data-testid={`connect-${l === "Cloud-URL" ? "url" : l.startsWith("Site") ? "key" : "plugin-key"}`} />
            <button className="btn !px-2" onClick={() => copy(v)} data-testid={`copy-${l === "Cloud-URL" ? "url" : l.startsWith("Site") ? "key" : "plugin-key"}`}><Copy size={13} /></button></div>
        </div>
      ))}
      <div className="text-[11px] text-eb-muted">Backups: täglich + vor jedem Update lokal (wp-content/uploads/sunworker-guard, geschützt) und jede geänderte Datei als Version in der Sunworker-Cloud (Hetzner). Lokal: {site.local_backups?.length || 0} Backups gemeldet.</div>
    </div>
  );
}

function LogTab({ site }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { axios.get(`${API}/sites/${site.id}/log`).then((r) => setRows(r.data)); }, [site.id]);
  return (
    <div className="card font-mono text-[11px] space-y-0.5" data-testid="siteguard-log">
      {rows.map((r) => (
        <div key={r.id} className="flex gap-3 py-1 border-b border-white/5">
          <span className="text-eb-muted shrink-0">{r.at.slice(0, 19).replace("T", " ")}</span>
          <span className="text-eb-primary w-20 shrink-0">{r.actor}</span><span className="w-20 shrink-0">{r.action}</span>
          <span className="text-eb-muted">{r.detail}</span>
        </div>
      ))}
    </div>
  );
}
